import { observable } from 'mobx';
import { UserInfoLocation } from '@app/src/components/user-management/model/Location';

/**
 * Holds the location id sent as the `X-Location-Id` header on parking-management requests.
 * Backed by a MobX observable so React surfaces (the selector, the parking content) can
 * react to a change and refresh in place — no full page reload. It is set at bootstrap in
 * AuthStore.initCurrentUser and updated when the user picks a location.
 *
 * The module has no store/repository imports (only mobx, a leaf dependency), so the axios
 * interceptor in BaseRepository can read it without a circular dependency.
 */
const currentLocationId = observable.box<string | null>(null);

export const setLocationRequestHeader = (id: string | null): void => {
  currentLocationId.set(id);
};

export const getLocationRequestHeader = (): string | null =>
  currentLocationId.get();

/**
 * Resolves the active location for the caller: the stored preference (if it still belongs
 * to the accessible locations), otherwise the company's main location, otherwise the first
 * location in the list, otherwise null.
 */
export const resolveActiveLocationId = (
  locations: UserInfoLocation[],
  storedId?: string
): string | null => {
  if (!locations || locations.length === 0) {
    return null;
  }
  if (storedId && locations.some(location => location.id === storedId)) {
    return storedId;
  }
  const mainLocation = locations.find(location => location.isMain);
  return (mainLocation || locations[0]).id;
};
